import React, { useState, useEffect } from 'react';
import { RetentionPolicy, DispositionResult } from '../../types';
import { dispositionService } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';

// Retention/expiry policy (Phase 3 disposition): per-domain retention with
// a '*' fallback, plus a manually triggered disposition run. A vCon's
// effective retention is the MINIMUM among its parties' domains — the
// strictest rule wins. Expiry hard-deletes the vCon (and its WAV when
// configured); the lifecycle ledger keeps the tombstone.
const RetentionPanel: React.FC = () => {
  const [policies, setPolicies] = useState<RetentionPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newDomain, setNewDomain] = useState('');
  const [newDays, setNewDays] = useState('');
  const [newDeleteWav, setNewDeleteWav] = useState(true);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDays, setEditDays] = useState('');
  const [editDeleteWav, setEditDeleteWav] = useState(true);

  const [runBusy, setRunBusy] = useState(false);
  const [runResult, setRunResult] = useState<DispositionResult | null>(null);
  const [confirmRealRun, setConfirmRealRun] = useState(false);

  const fetchPolicies = async () => {
    setLoading(true);
    setError(null);
    try {
      setPolicies(await dispositionService.listPolicies());
    } catch (err) {
      setError('Failed to fetch retention policies');
      console.error('Error fetching retention policies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const parseDays = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = parseInt(trimmed, 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await dispositionService.savePolicy(newDomain.trim(), parseDays(newDays), newDeleteWav);
      setNewDomain('');
      setNewDays('');
      setNewDeleteWav(true);
      fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save policy');
    }
  };

  const startEditing = (policy: RetentionPolicy) => {
    setEditingId(policy.id);
    setEditDays(policy.retention_days === null ? '' : String(policy.retention_days));
    setEditDeleteWav(policy.delete_wav);
  };

  const saveEditing = async (policy: RetentionPolicy) => {
    setError(null);
    try {
      await dispositionService.savePolicy(policy.domain, parseDays(editDays), editDeleteWav);
      setEditingId(null);
      fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save policy');
    }
  };

  const handleDelete = async (domain: string) => {
    setError(null);
    try {
      await dispositionService.deletePolicy(domain);
      fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete policy');
    }
  };

  const handleRun = async (dryRun: boolean) => {
    setError(null);
    setRunBusy(true);
    setConfirmRealRun(false);
    try {
      setRunResult(await dispositionService.run(dryRun));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Disposition run failed');
    } finally {
      setRunBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading retention policy...</div>;

  return (
    <div className="settings-panel">
      <h3>Retention &amp; expiry</h3>
      <p className="settings-hint">
        vCons past retention are expired: ledgered as <span className="monospace">vcon_expired</span>,
        then hard-deleted (the ledger is the tombstone). A vCon's retention is the strictest
        (shortest) rule among its parties' domains; the * fallback governs unconfigured domains.
        Empty days = keep forever. On the VM a daily timer runs this automatically; on the Mac use
        Run now.
      </p>
      {error && <div className="error">{error}</div>}

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Retention (days)</th>
              <th>Delete WAV too</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {policies.map(policy => (
              <tr key={policy.id}>
                <td className="monospace">{policy.domain}</td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="number"
                      min={1}
                      value={editDays}
                      onChange={e => setEditDays(e.target.value)}
                      placeholder="never"
                      style={{ width: '6em' }}
                    />
                  ) : (
                    policy.retention_days === null ? 'never expires' : policy.retention_days
                  )}
                </td>
                <td>
                  {editingId === policy.id ? (
                    <input
                      type="checkbox"
                      checked={editDeleteWav}
                      onChange={e => setEditDeleteWav(e.target.checked)}
                    />
                  ) : (
                    policy.delete_wav ? 'yes' : 'no'
                  )}
                </td>
                <td>{formatDateTime(policy.updated_at)}</td>
                <td>
                  {editingId === policy.id ? (
                    <>
                      <button type="button" onClick={() => saveEditing(policy)}>Save</button>
                      <button type="button" onClick={() => setEditingId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => startEditing(policy)}>Edit</button>
                      {policy.domain !== '*' && (
                        <button type="button" onClick={() => handleDelete(policy.domain)}>Remove</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={handleAdd} className="search-form settings-form">
        <input
          type="text"
          placeholder="Domain, e.g. pbx.example.com"
          value={newDomain}
          onChange={e => setNewDomain(e.target.value)}
          required
        />
        <input
          type="number"
          min={1}
          placeholder="Days (empty = never)"
          value={newDays}
          onChange={e => setNewDays(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={newDeleteWav}
            onChange={e => setNewDeleteWav(e.target.checked)}
          />
          {' '}delete WAV too
        </label>
        <button type="submit">Add override</button>
      </form>

      <h3 style={{ marginTop: '1.5rem' }}>Disposition run</h3>
      <p className="settings-hint">
        Preview shows what the current policy would expire without touching anything.
      </p>
      <div className="settings-form">
        <button type="button" disabled={runBusy} onClick={() => handleRun(true)}>
          {runBusy ? 'Running…' : 'Preview (dry run)'}
        </button>
        {!confirmRealRun ? (
          <button type="button" disabled={runBusy} onClick={() => setConfirmRealRun(true)}>
            Run now…
          </button>
        ) : (
          <>
            <span className="error" style={{ margin: '0 0.5rem' }}>
              This permanently deletes every vCon (and configured WAVs) past retention.
            </span>
            <button type="button" disabled={runBusy} onClick={() => handleRun(false)}>
              Confirm run
            </button>
            <button type="button" onClick={() => setConfirmRealRun(false)}>Cancel</button>
          </>
        )}
      </div>

      {runResult && (
        <div style={{ marginTop: '0.75rem' }}>
          <p className="settings-hint">
            {runResult.dry_run ? 'Dry run' : 'Run'} at {formatDateTime(runResult.ran_at)} —
            scanned {runResult.scanned} vCon{runResult.scanned === 1 ? '' : 's'};{' '}
            {runResult.expired.length} {runResult.dry_run ? 'would expire' : 'expired'};{' '}
            {runResult.consent_expired.length} lapsed consent grant{runResult.consent_expired.length === 1 ? '' : 's'}
            {runResult.dry_run ? ' would be' : ''} revoked.
            {runResult.errors.length > 0 && ` ${runResult.errors.length} error(s).`}
          </p>
          {runResult.expired.length > 0 && (
            <div className="table-container">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>vCon</th>
                    <th>Age (days)</th>
                    <th>Retention</th>
                    <th>Governing domain</th>
                    <th>WAV</th>
                  </tr>
                </thead>
                <tbody>
                  {runResult.expired.map((e, i) => (
                    <tr key={i}>
                      <td className="monospace small">{e.filename}</td>
                      <td>{e.age_days}</td>
                      <td>{e.retention_days}</td>
                      <td className="monospace small">{e.governing_domain || '—'}</td>
                      <td>{e.wav_deleted ? (runResult.dry_run ? 'would delete' : 'deleted') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {runResult.errors.length > 0 && (
            <div className="error">{runResult.errors.join('; ')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default RetentionPanel;
